import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useAppForm } from '@/lib/forms/hook'

const tableSchema = z.object({
  tableNumber: z.string().min(1, 'กรุณาระบุเลขโต๊ะ หรือชื่อจุด'),
  capacity: z.number().int().optional(),
  notes: z.string(),
  type: z.enum(['table', 'stage', 'registration', 'other']),
  shape: z.enum(['circle', 'square', 'rectangle']),
  width: z.number().optional(),
  height: z.number().optional(),
})

export type TableFormValues = z.infer<typeof tableSchema>

interface TableFormDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: TableFormValues) => Promise<void>
  initialValues?: Partial<TableFormValues> & { id?: string }
  isPending: boolean
}

interface TableInternalFormProps {
  initialValues?: Partial<TableFormValues> & { id?: string }
  onSubmit: (values: TableFormValues) => Promise<void>
  onClose: () => void
  isPending: boolean
}

const TableInternalForm = ({
  initialValues,
  onSubmit,
  onClose,
  isPending,
}: TableInternalFormProps) => {
  const isEdit = !!initialValues?.id
  const form = useAppForm({
    defaultValues: {
      tableNumber: initialValues?.tableNumber || '',
      capacity: initialValues?.capacity ?? 10,
      notes: initialValues?.notes || '',
      type: initialValues?.type || 'table',
      shape: initialValues?.shape || 'circle',
      width: initialValues?.width || 60,
      height: initialValues?.height || 60,
    } as TableFormValues,
    validators: {
      onChange: tableSchema,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value)
      onClose()
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="flex flex-col gap-5"
    >
      <form.AppField name="type">
        {(field) => (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold">ประเภท</label>
            <select
              value={field.state.value}
              onChange={(e) =>
                field.handleChange(e.target.value as typeof field.state.value)
              }
              className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-11 w-full rounded-xl border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="table">โต๊ะ (Table)</option>
              <option value="stage">เวที (Stage)</option>
              <option value="registration">โต๊ะลงทะเบียน (Registration)</option>
              <option value="other">อื่นๆ (Other)</option>
            </select>
          </div>
        )}
      </form.AppField>
      <form.AppField name="tableNumber">
        {(field) => (
          <field.TextField
            label="เลขโต๊ะ / ชื่อจุด"
            placeholder="เช่น 1, A1, Stage"
          />
        )}
      </form.AppField>

      <form.Subscribe selector={(state) => state.values.type}>
        {(type) => (
          <form.AppField name="capacity">
            {(field) => (
              <div className={type === 'table' ? 'block' : 'hidden'}>
                <field.TextField
                  label="จำนวนที่นั่ง"
                  type="number"
                  placeholder="10"
                />
              </div>
            )}
          </form.AppField>
        )}
      </form.Subscribe>

      <form.AppField name="notes">
        {(field) => (
          <field.TextField
            label="หมายเหตุ (ถ้ามี)"
            placeholder="เช่น ใกล้เวที, ติดทางเดิน"
          />
        )}
      </form.AppField>

      <div className="flex flex-col gap-3 pt-6">
        <form.AppForm>
          <form.SubmitButton
            label={isEdit ? 'บันทึกการแก้ไข' : 'สร้างโต๊ะ'}
            loadingLabel="กำลังบันทึก..."
          />
        </form.AppForm>
        <Button
          type="button"
          variant="ghost"
          className="rounded-full"
          onClick={onClose}
          disabled={isPending}
        >
          ยกเลิก
        </Button>
      </div>
    </form>
  )
}

export const TableFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  isPending,
}: TableFormDialogProps) => {
  const isMobile = useIsMobile()
  const isEdit = !!initialValues?.id

  // Frozen values and stable key logic to handle closing animations smoothly
  const [activeValues, setActiveValues] = useState(initialValues)
  const [formKey, setFormKey] = useState(0)
  const prevOpenRef = useRef(isOpen)

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      // Just opened: update values and increment key to trigger fresh mount
      setActiveValues(initialValues)
      setFormKey((prev) => prev + 1)
    }
    prevOpenRef.current = isOpen
  }, [isOpen, initialValues])

  const internalForm = (
    <TableInternalForm
      key={`${activeValues?.id || 'new'}-${formKey}`}
      initialValues={activeValues}
      onSubmit={onSubmit}
      onClose={onClose}
      isPending={isPending}
    />
  )

  if (isMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
      >
        <DrawerContent className="max-h-[96dvh]">
          <div className="mx-auto flex h-full w-full max-w-lg flex-col overflow-hidden">
            <div className="p-6 pb-2">
              <DrawerHeader className="mb-6 flex flex-row items-center justify-between p-0">
                <DrawerTitle className="text-xl font-bold">
                  {isEdit ? 'แก้ไขข้อมูลโต๊ะ' : 'เพิ่มโต๊ะใหม่'}
                </DrawerTitle>
                <DrawerClose className="bg-muted text-muted-foreground hover:text-foreground rounded-full p-2 transition-colors">
                  <X className="size-5" />
                </DrawerClose>
              </DrawerHeader>
              <DrawerDescription className="sr-only">
                {isEdit ? 'แก้ไขข้อมูลโต๊ะ' : 'เพิ่มโต๊ะใหม่ลงในระบบ'}
              </DrawerDescription>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {internalForm}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="wedding-card sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {isEdit ? 'แก้ไขข้อมูลโต๊ะ' : 'เพิ่มโต๊ะใหม่'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? 'แก้ไขข้อมูลโต๊ะ' : 'เพิ่มโต๊ะใหม่ลงในระบบ'}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">{internalForm}</div>
      </DialogContent>
    </Dialog>
  )
}
